/**
 * Team Comms - Shared Constants
 * 
 * Contains all shared constants used across Team Comms components.
 */

export const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Avatar gradient colors for user avatars
 * Applied based on name hash for consistency
 */
export const AVATAR_GRADIENTS = [
  'from-indigo-500 to-purple-500',
  'from-blue-500 to-cyan-500',
  'from-emerald-500 to-teal-500',
  'from-orange-500 to-rose-500',
  'from-pink-500 to-purple-500',
  'from-violet-500 to-indigo-500',
  'from-cyan-500 to-blue-500',
  'from-rose-500 to-pink-500'
];

/**
 * Get gradient class based on name for consistent avatar colors
 * @param {string} name - User's name
 * @returns {string} Tailwind gradient class
 */
export const getAvatarGradient = (name) => {
  if (!name) return AVATAR_GRADIENTS[0];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

/**
 * Reaction emojis for posts and comments
 */
export const REACTIONS = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡'
};

/** Quick-row options for the reaction pickers (posts and comments). */
export const REACTION_OPTIONS = Object.entries(REACTIONS).map(([key, emoji]) => ({ key, emoji }));

/**
 * Reaction key → what to draw. The six legacy keys map through REACTIONS;
 * any other key IS the emoji (chosen from the full picker, 2026-09-09) and
 * is drawn as itself. Never returns undefined for a stored key.
 */
export const getReactionEmoji = (key) => (Object.prototype.hasOwnProperty.call(REACTIONS, key) ? REACTIONS[key] : key) || '';

/** True for one of the six legacy keys (own property only). */
export const isLegacyReactionKey = (key) => Object.prototype.hasOwnProperty.call(REACTIONS, key);

/**
 * Get day with ordinal suffix (1st, 2nd, 3rd, etc.)
 * @param {number} day - Day of month
 * @returns {string} Day with suffix
 */
export const getDayWithSuffix = (day) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};
